import { Link, useParams } from 'react-router-dom';
import {
  useGameCatalog,
  useUpdateGameCatalog,
  useAddGalleryAsset,
  useRemoveGalleryAsset,
} from './game-catalog-queries';
import { GameCatalogForm } from './game-catalog-form';
import { GalleryManager } from './components/gallery-manager';
import { GalleryAsset } from '../games/games-types';
import { useState } from 'react';

/**
 * v0.3.0 Game Catalog page. Route: /games/:id/catalog
 *
 * Composition: GameCatalogForm + GalleryManager. Renders catalog fields,
 * lets admin edit any subset (PATCH semantics), and manage gallery uploads +
 * drag-drop reorder.
 */
export function GameCatalogPage() {
  const { id } = useParams<{ id: string }>();
  const gameId = id ?? '';

  const catalog = useGameCatalog(gameId);
  const update = useUpdateGameCatalog(gameId);
  const addAsset = useAddGalleryAsset(gameId);
  const removeAsset = useRemoveGalleryAsset(gameId);

  const [serverGallery, setServerGallery] = useState<GalleryAsset[]>([]);
  const [optimisticGallery, setOptimisticGallery] = useState<GalleryAsset[]>([]);

  // Coerce null → undefined for the form (backend ignores null in PATCH; UI
  // shows empty fields for nulls).
  const formDefaults = catalog.data
    ? {
        description: catalog.data.description ?? undefined,
        icon_url: catalog.data.icon_url ?? undefined,
        ios_url: catalog.data.ios_url ?? undefined,
        android_url: catalog.data.android_url ?? undefined,
        web_url: catalog.data.web_url ?? undefined,
        harmonyos_url: catalog.data.harmonyos_url ?? undefined,
        website_url: catalog.data.website_url ?? undefined,
        status: catalog.data.status,
      }
    : undefined;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/games" className="text-sm text-blue-600 hover:underline">
          ← Games
        </Link>
        <h1 className="text-2xl font-semibold">
          {catalog.data ? catalog.data.name : 'Loading…'}
        </h1>
        {catalog.data && (
          <span className="text-sm text-gray-500 font-mono">
            ({catalog.data.package_name})
          </span>
        )}
      </div>

      {catalog.isLoading && <p className="text-sm text-gray-500">Loading catalog…</p>}
      {catalog.error && (
        <p className="text-sm text-red-600">Failed to load catalog: {String(catalog.error)}</p>
      )}

      {catalog.data && formDefaults && (
        <>
          <section className="mb-8 p-4 border rounded">
            <h2 className="font-medium mb-2">Marketing catalog</h2>
            <GameCatalogForm
              defaultValues={formDefaults}
              isSubmitting={update.isPending}
              onSubmit={(values) => update.mutate(values)}
            />
          </section>

          <section className="p-4 border rounded space-y-3">
            <h2 className="font-medium">Gallery</h2>
            <GalleryManager
              gameId={gameId}
              assets={serverGallery}
              onAdd={async (file) => {
                const inserted = await addAsset.mutateAsync(file);
                setServerGallery((prev) => [...prev, inserted]);
              }}
              onRemove={async (assetId) => {
                await removeAsset.mutateAsync(assetId);
                setServerGallery((prev) => prev.filter((a) => a.id !== assetId));
              }}
            />
            {optimisticGallery.length > 0 && (
              <GalleryManager
                gameId={gameId}
                assets={optimisticGallery}
                onAdd={() => {}}
                onRemove={(assetId) => {
                  if (assetId.startsWith('temp-')) {
                    setOptimisticGallery((prev) => prev.filter((a) => a.id !== assetId));
                  }
                }}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}